///---------------------------------------------------------------------------
//! Copyright (C) ASQUARED SRL - All Rights Reserved
//* Unauthorized copying of this file, via any medium is strictly prohibited
//* Proprietary and confidential
//* Written by Alexandru Gârbacea <g99.alex@yahoo.com>, October 2026
//? @author g99.alex@yahoo.com
///---------------------------------------------------------------------------

import { db } from './FirebaseService'
import { getDateFromTimestamp } from './DateService'
import { daysUntilExpiry, subscriptionUsage } from './SubscriptionsService'

//? the SDK in use still validates 'in' queries against the old limit of 10 values
const IN_QUERY_CHUNK = 10
const DAY_MS = 86400000

// ─── Fetchers ──────────────────────────────────────────────────────────────────────────
//? Every query here is either a single field range or equality-only, so none of them need
//? a composite index. They are only ever run when the admin opens the section using them.

const withId = (snapshot) => snapshot.docs.map(doc => ({ subId: doc.id, ...doc.data() }))

//-----------------------------------------------------------------------------------------
//* \brief
//? The subscription each user is currently on - or was last on.
//?
//? Ordering a new subscription disables the previous one (manualDisable), so a subscription
//? that is valid and not disabled is the latest one its user paid for (or the demo they got
//? at signup). Expired ones are kept in: an expired subscription still in this set is a user
//? who never renewed.
//-----------------------------------------------------------------------------------------
export const fetchCurrentSubscriptions = async () => {
    const snapshot = await db.collection('subscriptions')
        .where('valid', '==', true)
        .where('manualDisable', '==', false)
        .get()
    return withId(snapshot)
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Orders placed and not paid yet - created inactive, waiting for validation.
//-----------------------------------------------------------------------------------------
export const fetchPendingSubscriptions = async () => {
    const snapshot = await db.collection('subscriptions')
        .where('valid', '==', false)
        .where('manualDisable', '==', false)
        .get()
    return withId(snapshot)
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Every subscription ever created. The heaviest read here (one per document), only used
//? by the conversion / retention section, which needs the full history of each user.
//-----------------------------------------------------------------------------------------
export const fetchAllSubscriptions = async () => {
    const snapshot = await db.collection('subscriptions').get()
    return withId(snapshot)
}

export const fetchUnpaidInvoices = async () => {
    const snapshot = await db.collection('invoices').where('status', '==', '0').get()
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Invoices dated on or after a day. A paid invoice has its timestamp moved to the moment
//? it was paid, so for those the date read here is the payment date.
//-----------------------------------------------------------------------------------------
export const fetchInvoicesSince = async (from) => {
    const snapshot = await db.collection('invoices').where('timestamp', '>=', from).get()
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
}

//-----------------------------------------------------------------------------------------
//* \brief
//? user_info documents for a list of uids, keyed by uid. Only the uids not in `known` are
//? asked for, so sections opened one after another never read the same user twice.
//? A chunk that fails (rules, network) is skipped - the rows then only show the uid.
//-----------------------------------------------------------------------------------------
export const fetchUsersInfo = async (uids, known = {}) => {
    const missing = [...new Set(uids)].filter(uid => uid && !(uid in known))
    const found = {}
    for (let i = 0; i < missing.length; i += IN_QUERY_CHUNK) {
        const chunk = missing.slice(i, i + IN_QUERY_CHUNK)
        try {
            const snapshot = await db.collection('user_info').where('uid', 'in', chunk).get()
            snapshot.docs.forEach(doc => { found[doc.data().uid] = doc.data() })
        }
        catch (err) {
            console.error(err)
        }
        //? remembered as missing too, so a user without a profile is not asked for again
        chunk.forEach(uid => { if (!found[uid]) found[uid] = null })
    }
    return found
}

// ─── Subscription helpers ──────────────────────────────────────────────────────────────

//? sub.date is what every other calculation in the app is anchored to; timeStamp only
//? covers documents written without it
export const subStartDate = (sub) => {
    if (sub.date) {
        const date = new Date(sub.date)
        if (!isNaN(date.getTime())) return date
    }
    if (sub.timeStamp) return getDateFromTimestamp(sub.timeStamp)
    return null
}

export const subExpiryDate = (sub) => {
    const start = subStartDate(sub)
    const duration = parseInt(sub.duration)
    if (!start || isNaN(duration)) return null
    const expiry = new Date(start)
    expiry.setMonth(expiry.getMonth() + duration)
    return expiry
}

//? 0 = expires today (still usable all day), negative = already expired, null = unknown
export const subDaysLeft = (sub) => {
    const start = subStartDate(sub)
    const duration = parseInt(sub.duration)
    if (!start || isNaN(duration)) return null
    return daysUntilExpiry(start, duration)
}

export const isDemo = (sub) => parseInt(sub.name) === 0

//? EUR per month, without VAT - the same figure the proforma is priced from
export const subMonthlyPrice = (sub) => {
    const price = parseFloat(sub.price)
    return isNaN(price) ? 0 : price
}

//? what renewing for the same period would bring in (EUR, without VAT)
export const subContractValue = (sub) => subMonthlyPrice(sub) * (parseInt(sub.duration) || 0)

//? a paid plan given away for free is not revenue, so it is not counted as a paying customer
export const isPaying = (sub) => !isDemo(sub) && subMonthlyPrice(sub) > 0

const daysBetween = (from, to) => Math.floor((to - from) / DAY_MS)

//-----------------------------------------------------------------------------------------
//* \brief
//? Splits the current subscriptions into the states an owner acts on.
//?
//? A user can be left with more than one current subscription (an admin grant on top of a
//? purchase, for example), so a user only counts as lapsed when NONE of theirs is active,
//? and only their most recent expired one is listed.
//-----------------------------------------------------------------------------------------
export const classifyCurrent = (currentSubs, pendingSubs = []) => {
    const pendingByUid = {}
    pendingSubs.forEach(sub => { pendingByUid[sub.uid] = sub })

    const active = []
    const expiredByUid = {}
    const activeUids = new Set()

    currentSubs.forEach(sub => {
        const daysLeft = subDaysLeft(sub)
        if (daysLeft === null) return
        const row = { ...sub, daysLeft, expiry: subExpiryDate(sub), pending: pendingByUid[sub.uid] || null }
        if (daysLeft >= 0) {
            active.push(row)
            activeUids.add(sub.uid)
        }
        else {
            const prev = expiredByUid[sub.uid]
            if (!prev || prev.daysLeft < daysLeft) expiredByUid[sub.uid] = row
        }
    })

    const lapsed = Object.values(expiredByUid).filter(sub => !activeUids.has(sub.uid))
    return { active, lapsed, pendingByUid }
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Headline numbers for the overview.
//-----------------------------------------------------------------------------------------
export const buildOverview = (currentSubs, pendingSubs) => {
    const { active, lapsed } = classifyCurrent(currentSubs, pendingSubs)
    const paying = active.filter(isPaying)
    const sum = (arr, fn) => arr.reduce((acc, item) => acc + fn(item), 0)

    const byPlan = {}
    active.forEach(sub => {
        const key = sub.name
        if (!byPlan[key]) byPlan[key] = { name: key, count: 0, mrr: 0 }
        byPlan[key].count++
        byPlan[key].mrr += isPaying(sub) ? subMonthlyPrice(sub) : 0
    })

    const expiringPaid = (days) => paying.filter(sub => sub.daysLeft <= days)
    const lapsedPaid = (days) => lapsed.filter(sub => isPaying(sub) && -sub.daysLeft <= days)

    const mrr = sum(paying, subMonthlyPrice)
    return {
        payingCount: paying.length,
        mrr,
        arr: mrr * 12,
        avgMonthly: paying.length ? mrr / paying.length : 0,
        demoActive: active.filter(isDemo).length,
        freeActive: active.filter(sub => !isDemo(sub) && !isPaying(sub)).length,
        expiring7: expiringPaid(7).length,
        expiring30: expiringPaid(30).length,
        expiring30Value: sum(expiringPaid(30), subContractValue),
        expiring90: expiringPaid(90).length,
        expiring90Value: sum(expiringPaid(90), subContractValue),
        lapsed30: lapsedPaid(30).length,
        lapsed30Mrr: sum(lapsedPaid(30), subMonthlyPrice),
        lapsed90: lapsedPaid(90).length,
        demoExpiring14: active.filter(sub => isDemo(sub) && sub.daysLeft <= 14).length,
        pendingCount: pendingSubs.length,
        pendingValue: sum(pendingSubs, subContractValue),
        byPlan: Object.values(byPlan).sort((a, b) => b.count - a.count)
    }
}

//-----------------------------------------------------------------------------------------
//* \brief
//? How a subscription is being used - drives the churn-risk and upsell lists.
//?
//? 'risk'    : paying, past its first month, and nothing used this month nor last month
//? 'upsell'  : ran out of lines in at least one month, or is already at 90% this month
//-----------------------------------------------------------------------------------------
export const usageHealth = (sub) => {
    const usage = subscriptionUsage(sub)
    if (!usage) return { usage: null, flag: null, currentPct: null, avgPct: null }

    const pct = (value) => usage.allowance > 0 ? Math.round(value / usage.allowance * 100) : null
    const lastCompleted = usage.months.filter(month => !month.isCurrent).slice(-1)[0]
    const currentPct = pct(usage.current.used)
    const avgPct = usage.averageUsed === null ? null : pct(usage.averageUsed)

    let flag = null
    if (usage.exhaustedMonths > 0 || currentPct >= 90) flag = 'upsell'
    else if (lastCompleted && lastCompleted.used === 0 && usage.current.used === 0) flag = 'risk'

    return { usage, flag, currentPct, avgPct }
}

// ─── Revenue ───────────────────────────────────────────────────────────────────────────

const monthKey = (date) => `${date.getFullYear()}-${date.getMonth()}`

//? the last `count` calendar months, oldest first, ending with the current one
export const lastMonths = (count, now = new Date()) => {
    const months = []
    for (let i = count - 1; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
        months.push({ key: monthKey(date), date })
    }
    return months
}

//? the next `count` calendar months, starting with the current one
export const nextMonths = (count, now = new Date()) => {
    const months = []
    for (let i = 0; i < count; i++) {
        const date = new Date(now.getFullYear(), now.getMonth() + i, 1)
        months.push({ key: monthKey(date), date })
    }
    return months
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Invoices bucketed per calendar month. Amounts are the invoice price (Lei, no VAT).
//-----------------------------------------------------------------------------------------
export const revenueByMonth = (invoices, months) => {
    const buckets = {}
    months.forEach(({ key, date }) => {
        buckets[key] = { key, date, paid: 0, paidCount: 0, unpaid: 0, unpaidCount: 0, canceledCount: 0 }
    })
    invoices.forEach(inv => {
        const date = getDateFromTimestamp(inv.timestamp)
        if (!date) return
        const bucket = buckets[monthKey(date)]
        if (!bucket) return
        const price = parseFloat(inv.price) || 0
        if (inv.status === '1') { bucket.paid += price; bucket.paidCount++ }
        else if (inv.status === '0') { bucket.unpaid += price; bucket.unpaidCount++ }
        else if (inv.status === '2') bucket.canceledCount++
    })
    return months.map(({ key }) => buckets[key])
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Renewals coming up per calendar month: what would be invoiced if every paying customer
//? renewed for the same period at the same price (EUR, no VAT).
//-----------------------------------------------------------------------------------------
export const renewalForecast = (currentSubs, months) => {
    const buckets = {}
    months.forEach(({ key, date }) => { buckets[key] = { key, date, count: 0, value: 0, demoCount: 0 } })
    currentSubs.forEach(sub => {
        const daysLeft = subDaysLeft(sub)
        if (daysLeft === null || daysLeft < 0) return
        const bucket = buckets[monthKey(subExpiryDate(sub))]
        if (!bucket) return
        if (isPaying(sub)) { bucket.count++; bucket.value += subContractValue(sub) }
        else if (isDemo(sub)) bucket.demoCount++
    })
    return months.map(({ key }) => buckets[key])
}

// ─── Conversion & retention ───────────────────────────────────────────────────────────

//-----------------------------------------------------------------------------------------
//* \brief
//? Signups, first purchases and renewals per month, plus renewal rate, over the full
//? subscription history.
//?
//? A signup is the demo subscription every account gets on creation. A purchase is any
//? non-demo subscription that was activated (valid) - the first one per user is a
//? conversion, every later one a renewal / upgrade.
//-----------------------------------------------------------------------------------------
export const conversionStats = (allSubs, monthsCount = 12, now = new Date()) => {
    const months = lastMonths(monthsCount, now)
    const buckets = {}
    months.forEach(({ key, date }) => {
        buckets[key] = { key, date, signups: 0, converted: 0, firstPurchases: 0, renewals: 0 }
    })

    const byUid = {}
    allSubs.forEach(sub => {
        const start = subStartDate(sub)
        if (!start || !sub.uid) return
        if (!byUid[sub.uid]) byUid[sub.uid] = []
        byUid[sub.uid].push({ ...sub, start })
    })

    const daysToConvert = []
    let renewDue = 0
    let renewDone = 0
    const windowStart = months[0].date

    Object.values(byUid).forEach(subs => {
        subs.sort((a, b) => a.start - b.start)
        const demo = subs.find(isDemo)
        const purchases = subs.filter(sub => !isDemo(sub) && sub.valid === true && isPaying(sub))
        const firstPurchase = purchases[0]

        if (demo) {
            const bucket = buckets[monthKey(demo.start)]
            if (bucket) {
                bucket.signups++
                if (firstPurchase) bucket.converted++
            }
            if (firstPurchase) daysToConvert.push(Math.max(0, daysBetween(demo.start, firstPurchase.start)))
        }

        purchases.forEach((sub, i) => {
            const bucket = buckets[monthKey(sub.start)]
            if (bucket) i === 0 ? bucket.firstPurchases++ : bucket.renewals++

            //? renewal rate: of the paid periods that ended inside the window, how many were
            //? followed by another purchase from the same user
            const expiry = subExpiryDate(sub)
            if (expiry && expiry >= windowStart && expiry <= now) {
                renewDue++
                if (purchases.slice(i + 1).length > 0) renewDone++
            }
        })
    })

    const rows = months.map(({ key }) => buckets[key])
    const totalSignups = rows.reduce((acc, row) => acc + row.signups, 0)
    const totalConverted = rows.reduce((acc, row) => acc + row.converted, 0)
    daysToConvert.sort((a, b) => a - b)

    return {
        rows,
        totalUsers: Object.keys(byUid).length,
        totalSignups,
        conversionRate: totalSignups ? totalConverted / totalSignups : null,
        medianDaysToConvert: daysToConvert.length ? daysToConvert[Math.floor(daysToConvert.length / 2)] : null,
        renewDue,
        renewDone,
        renewalRate: renewDue ? renewDone / renewDue : null
    }
}
